/* -------------------------------------------------------------------------- */
/*                         VideoPlayerComponent                             */
/* -------------------------------------------------------------------------- */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import Hls from 'hls.js';
import { Spinner, Alert } from 'reactstrap';
import Icon from 'utility/icomoon';
import { env } from '@src/env';

interface Props {
  streamUrl?: string;
  isStreaming: boolean;
  isLoading: boolean;
  onStatsUpdate?: (stats: any) => void;
  onError: (error: string) => void;
  onBytesLoaded?: (bytes: number) => void;
}

interface VideoStats {
  downloadSpeed: number;
  quality: string;
  resolution: string;
  bitrate: number;
}

const VideoPlayerComponent: React.FC<Props> = ({
  streamUrl,
  isStreaming,
  isLoading,
  onStatsUpdate,
  onError,
  onBytesLoaded
}) => {
  const intl = useIntl();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [videoError, setVideoError] = useState<string>('');
  const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [playPromise, setPlayPromise] = useState<Promise<void> | null>(null);
  const maxRetries = 3;

  // Estados para indicador STREAMING
  const [isLiveStream, setIsLiveStream] = useState<boolean>(false);
  const [streamingStatus, setStreamingStatus] = useState<'current' | 'delayed' | 'offline'>('offline');
  const [secondsBehind, setSecondsBehind] = useState<number>(0);
  const liveCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Umbrales para determinar el estado del streaming (con histéresis para evitar parpadeo)
  const CURRENT_THRESHOLD = 9; // Umbral para cambiar de LIVE (rojo) a delayed (gris)
  const BACK_TO_LIVE_THRESHOLD = 7; // Umbral para volver de delayed (gris) a LIVE (rojo)
  // Esto crea una "zona de histéresis" entre 7s y 9s para evitar parpadeos

  // Función para verificar el estado del streaming
  const checkStreamingStatus = useCallback(() => {
    if (!videoRef.current || !hlsRef.current || !isLiveStream) return;

    const video = videoRef.current;
    const hls = hlsRef.current;

    // Calcular cuánto nos falta para llegar al final disponible
    const liveSyncPosition = hls.liveSyncPosition;
    const currentTime = video.currentTime;

    if (liveSyncPosition !== undefined && !isNaN(liveSyncPosition)) {
      const behind = liveSyncPosition - currentTime;
      setSecondsBehind(behind);

      // Determinar el estado basado en el retraso con histéresis para evitar parpadeo
      // Si está en "current" (rojo), necesita superar 9s para cambiar a "delayed" (gris)
      // Si está en "delayed" (gris), necesita bajar de 7s para volver a "current" (rojo)
      if (streamingStatus === 'current') {
        // Actualmente en LIVE (rojo), cambiar a delayed solo si supera 9s
        if (behind >= CURRENT_THRESHOLD) {
          setStreamingStatus('delayed'); // Cambiar a gris
        }
      } else {
        // Actualmente en delayed (gris), volver a LIVE solo si baja de 7s
        if (behind < BACK_TO_LIVE_THRESHOLD) {
          setStreamingStatus('current'); // Cambiar a rojo
        }
      }
    }
  }, [isLiveStream, streamingStatus, CURRENT_THRESHOLD, BACK_TO_LIVE_THRESHOLD]);

  // Función para saltar al contenido más reciente disponible
  const goToLatest = useCallback(() => {
    if (!videoRef.current || !hlsRef.current || !isLiveStream) return;

    const video = videoRef.current;
    const hls = hlsRef.current;

    // Obtener la posición más reciente disponible
    const liveSyncPosition = hls.liveSyncPosition;

    if (liveSyncPosition !== undefined && !isNaN(liveSyncPosition)) {
      // Saltar a la posición más reciente
      video.currentTime = liveSyncPosition;
      setStreamingStatus('current');
      setSecondsBehind(0);
      console.log('Jumped to latest available position:', liveSyncPosition);
    }
  }, [isLiveStream]);

  // Función para reproducir video de manera segura
  const safePlay = useCallback(async () => {
    if (!videoRef.current) return;

    try {
      // Cancelar play anterior si existe
      if (playPromise) {
        try {
          await playPromise;
        } catch (error) {
          // Ignorar errores de la promesa anterior
        }
      }

      // Iniciar nueva reproducción
      const promise = videoRef.current.play();
      setPlayPromise(promise);

      await promise;
      setPlayPromise(null);
      console.log('Video started playing successfully');
    } catch (error: any) {
      setPlayPromise(null);

      if (error.name === 'AbortError') {
        console.log('Play interrupted by another play request, this is normal');
        return;
      }

      console.error('Error playing video:', error);
      const errorMsg = intl.formatMessage({ id: 'units.videoPlayer.error' });
      setVideoError(errorMsg);
      onError(errorMsg);
    }
  }, [playPromise, intl]);

  // Función para limpiar recursos HLS
  const cleanupHls = useCallback(() => {
    console.log('Cleaning up HLS resources');
    if (hlsRef.current) {
      try {
        hlsRef.current.destroy();
        console.log('HLS instance destroyed');
      } catch (error) {
        console.warn('Error destroying HLS instance:', error);
      }
      hlsRef.current = null;
    }
    setIsVideoReady(false);
    setVideoError('');
    setPlayPromise(null);
  }, []);

  // Inicializar HLS
  useEffect(() => {
    console.log('HLS useEffect triggered:', {
      isStreaming,
      streamUrl,
      hasVideoRef: !!videoRef.current
    });

    if (!videoRef.current) return;

    const video = videoRef.current;

    // Limpiar instancia anterior
    cleanupHls();

    if (isStreaming && streamUrl) {
      console.log('Initializing HLS with URL:', streamUrl);

      if (Hls.isSupported()) {
        try {
          // Crear nueva instancia de HLS con configuración moderna
          const hls = new Hls({
            debug: env.MODE === 'development',

            enableWorker: true,
            lowLatencyMode: false,
            backBufferLength: 60,
            maxBufferLength: 30,
            maxMaxBufferLength: 60,
            maxBufferHole: 1.5,
            liveSyncDuration: 8,
            startLevel: -1,
            capLevelToPlayerSize: true
          });

          hlsRef.current = hls;

          // Event listeners mejorados para streams problemáticos
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log('HLS: Manifest parsed successfully');
            setIsVideoReady(true);
            setVideoError('');
            setRetryCount(0);

            // Asumir que es un stream en vivo (siempre mostrar badge)
            setIsLiveStream(true);
            setStreamingStatus('current');
            console.log('HLS: Stream detected, showing LIVE badge');

            // Iniciar chequeo periódico del estado del streaming
            if (liveCheckIntervalRef.current) {
              clearInterval(liveCheckIntervalRef.current);
            }
            liveCheckIntervalRef.current = setInterval(checkStreamingStatus, 1000);

            // Delay antes de play para dar tiempo al buffer
            setTimeout(() => {
              if (video && hlsRef.current && videoRef.current) {
                const playVideo = async () => {
                  try {
                    if (playPromise) {
                      try {
                        await playPromise;
                      } catch (error) {
                        // Ignorar errores de la promesa anterior
                      }
                    }

                    const promise = videoRef.current!.play();
                    setPlayPromise(promise);

                    await promise;
                    setPlayPromise(null);
                    console.log('Video started playing successfully');
                  } catch (error: any) {
                    setPlayPromise(null);

                    if (error.name === 'AbortError') {
                      console.log('Play interrupted by another play request, this is normal');
                      return;
                    }

                    console.error('Error playing video:', error);
                    const errorMsg = intl.formatMessage({ id: 'units.videoPlayer.error' });
                    setVideoError(errorMsg);
                    onError(errorMsg);
                  }
                };
                playVideo();
              }
            }, 500);
          });

          hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
            const level = hls.levels[data.level];
            if (level) {
              console.log('HLS: Level switched to:', level.height + 'p');
              // Opcional: actualizar estadísticas si se necesita
              if (onStatsUpdate) {
                onStatsUpdate({
                  quality: level.height ? `${level.height}p` : 'Auto',
                  resolution:
                    level.width && level.height ? `${level.width}x${level.height}` : 'Unknown',
                  bitrate: Math.round((level.bitrate || 0) / 1000)
                });
              }
            }
          });

          hls.on(Hls.Events.FRAG_LOADED, (event, data) => {
            // Stream está funcionando
            setVideoError('');

            // Estadísticas de descarga basadas en el fragmento cargado
            if (data.frag && data.frag.stats && onBytesLoaded) {
              const bytes = data.frag.stats.loaded || 0;
              if (bytes > 0) {
                onBytesLoaded(bytes);
              }

              // Calcular velocidad de descarga con validación
              const loadingTime = data.frag.stats.loading.end - data.frag.stats.loading.start;
              if (loadingTime > 0 && !isNaN(loadingTime) && onStatsUpdate) {
                const downloadSpeed = Math.round(data.frag.stats.loaded / loadingTime / 1024);
                onStatsUpdate({
                  downloadSpeed: downloadSpeed > 0 ? downloadSpeed : 0
                });
              }
            }

            // Intentar reproducir si el video está pausado
            if (videoRef.current && videoRef.current.paused && isStreaming) {
              console.log('Video is paused, attempting to resume');
              const resumeVideo = async () => {
                try {
                  if (playPromise) {
                    try {
                      await playPromise;
                    } catch (error) {
                      // Ignorar errores de la promesa anterior
                    }
                  }

                  const promise = videoRef.current!.play();
                  setPlayPromise(promise);

                  await promise;
                  setPlayPromise(null);
                  console.log('Video resumed playing successfully');
                } catch (error: any) {
                  setPlayPromise(null);

                  if (error.name === 'AbortError') {
                    console.log('Resume play interrupted, this is normal');
                    return;
                  }

                  console.error('Error resuming video:', error);
                }
              };
              resumeVideo();
            }
          });

          hls.on(Hls.Events.ERROR, (event, data) => {
            console.error('HLS Error:', data);

            if (data.fatal) {
              let errorMessage = '';
              let shouldRetry = false;

              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  errorMessage = intl.formatMessage({ id: 'units.videoPlayer.connectionError' });
                  shouldRetry = true;
                  console.log('Fatal network error, attempting recovery');
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  errorMessage = intl.formatMessage({ id: 'units.videoPlayer.error' });
                  shouldRetry = true;
                  console.log('Fatal media error, attempting recovery');
                  break;
                default:
                  errorMessage = intl.formatMessage({ id: 'units.videoPlayer.error' });
                  console.log('Other fatal error, destroying HLS');
                  break;
              }

              // Intentar recuperación automática
              if (shouldRetry && retryCount < maxRetries) {
                setRetryCount((prev) => prev + 1);
                console.log(`Attempting HLS recovery (${retryCount + 1}/${maxRetries})`);

                setTimeout(() => {
                  if (hlsRef.current) {
                    try {
                      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                        hlsRef.current.startLoad();
                      } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                        hlsRef.current.recoverMediaError();
                      }
                    } catch (recoveryError) {
                      console.error('Recovery failed:', recoveryError);
                      cleanupHls();
                    }
                  }
                }, 2000 * (retryCount + 1)); // Delay incremental
              } else {
                console.log('Max retries reached or unrecoverable error');
                setVideoError(errorMessage);
                onError(errorMessage);
                cleanupHls();
              }
            } else {
              // Errores no fatales - manejo mejorado
              console.warn('Non-fatal HLS error:', data.details, data.reason || '');

              // Manejo específico para errores de fragmentos faltantes
              if (data.details === Hls.ErrorDetails.FRAG_LOAD_ERROR) {
                if (data.response?.code === 404) {
                  console.warn('Fragment not found (404), HLS will try next fragment');
                  // No mostrar error al usuario, es normal en streams live
                  return;
                }
              }

              // Manejo específico para errores de secuencia de media
              if (
                data.details === Hls.ErrorDetails.LEVEL_LOAD_ERROR &&
                data.response?.code === 404
              ) {
                console.warn('Level/manifest not found (404), will retry');
                // No mostrar error al usuario, HLS reintentará
                return;
              }

              // Manejo de errores de buffer
              if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
                console.warn('Buffer stalled, HLS will handle recovery');
                // No mostrar error al usuario, HLS maneja automáticamente
                return;
              }

              // Manejo de errores de decodificación
              if (data.details === Hls.ErrorDetails.FRAG_PARSING_ERROR) {
                console.warn('Fragment parsing error, may be temporary');
                // No mostrar error al usuario para errores temporales
                return;
              }

              // Para otros errores no fatales que podrían requerir atención del usuario
              if (
                data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
                data.details === Hls.ErrorDetails.LEVEL_LOAD_TIMEOUT
              ) {
                console.warn('Manifest/level loading issues, may indicate server problems');
                // Solo mostrar después de varios errores consecutivos
                setRetryCount((prev) => {
                  const newCount = prev + 1;
                  if (newCount >= 3) {
                    const errorMsg = intl.formatMessage({
                      id: 'units.videoPlayer.connectionError'
                    });
                    setVideoError(errorMsg);
                    onError(errorMsg);
                  }
                  return newCount;
                });
              }
            }
          });

          // Eventos adicionales para debugging
          hls.on(Hls.Events.MEDIA_ATTACHED, () => {
            console.log('HLS: Media attached');
          });

          hls.on(Hls.Events.BUFFER_APPENDED, () => {
            console.log('HLS: Buffer appended');
          });

          // Cargar el stream
          hls.loadSource(streamUrl);
          hls.attachMedia(video);
        } catch (error) {
          console.error('Error initializing HLS:', error);
          const errorMsg = intl.formatMessage({ id: 'units.videoPlayer.error' });
          setVideoError(errorMsg);
          onError(errorMsg);
        }
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari nativo
        console.log('Using native HLS support (Safari)');
        video.src = streamUrl;
        setIsVideoReady(true);
        video.addEventListener('loadedmetadata', () => {
          const playNative = async () => {
            try {
              if (playPromise) {
                try {
                  await playPromise;
                } catch (error) {
                  // Ignorar errores de la promesa anterior
                }
              }

              const promise = video.play();
              setPlayPromise(promise);

              await promise;
              setPlayPromise(null);
              console.log('Native video started playing successfully');
            } catch (error: any) {
              setPlayPromise(null);

              if (error.name === 'AbortError') {
                console.log('Native play interrupted, this is normal');
                return;
              }

              console.error('Error playing native video:', error);
              const errorMsg = intl.formatMessage({ id: 'units.videoPlayer.error' });
              setVideoError(errorMsg);
              onError(errorMsg);
            }
          };
          playNative();
        });
      } else {
        const errorMsg = 'HLS no es soportado en este navegador';
        console.error(errorMsg);
        setVideoError(errorMsg);
        onError(errorMsg);
      }
    }

    return () => {
      console.log('HLS useEffect cleanup called');
      cleanupHls();
    };
  }, [isStreaming, streamUrl]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      // Limpiar intervalo de chequeo LIVE
      if (liveCheckIntervalRef.current) {
        clearInterval(liveCheckIntervalRef.current);
      }
    };
  }, []);

  return (
    <div
      className="video-player-container"
      style={{
        position: 'relative',
        width: '100%',
        height: '400px',
        backgroundColor: '#000',
        borderRadius: '8px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {isStreaming ? (
        <>
          <video
            ref={videoRef}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
            controls
            muted
            autoPlay
            playsInline
            onError={(e) => {
              console.error('Video element error:', e);
              const error = (e.target as HTMLVideoElement).error;
              if (error) {
                let errorMessage = 'Unknown error';
                switch (error.code) {
                  case MediaError.MEDIA_ERR_ABORTED:
                    errorMessage = intl.formatMessage({ id: 'units.videoPlayer.errorAborted' });
                    break;
                  case MediaError.MEDIA_ERR_NETWORK:
                    errorMessage = intl.formatMessage({ id: 'units.videoPlayer.errorNetwork' });
                    break;
                  case MediaError.MEDIA_ERR_DECODE:
                    errorMessage = intl.formatMessage({ id: 'units.videoPlayer.errorDecode' });
                    break;
                  case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                    errorMessage = intl.formatMessage({ id: 'units.videoPlayer.errorFormat' });
                    break;
                }
                setVideoError(errorMessage);
                onError(errorMessage);
              }
            }}
            onTimeUpdate={() => {
              // Actualizar estado de streaming cuando el usuario navega manualmente
              if (isLiveStream) {
                checkStreamingStatus();
              }
            }}
          />

          {/* Spinner de carga mientras se inicializa el streaming */}
          {(isStreaming && !isVideoReady) || isLoading ? (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 150,
                gap: '16px'
              }}
            >
              <Spinner
                style={{
                  width: '3rem',
                  height: '3rem',
                  borderWidth: '0.3em'
                }}
                color="light"
              />
              <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
                {!isVideoReady ? 'Conectando streaming...' : 'Cargando...'}
              </div>
            </div>
          ) : null}

          {/* Indicador LIVE tipo YouTube */}
          {isLiveStream && isVideoReady && (
            <div
              style={{
                position: 'absolute',
                top: '16px',
                left: '16px',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {/* Badge LIVE */}
              <div
                onClick={streamingStatus === 'delayed' ? goToLatest : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor:
                    streamingStatus === 'current'
                      ? 'rgba(220, 53, 69, 0.9)' // Rojo para "actual/live"
                      : 'rgba(108, 117, 125, 0.9)', // Gris para "retrasado"
                  color: 'white',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: streamingStatus === 'delayed' ? 'pointer' : 'default',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  userSelect: 'none'
                }}
                onMouseEnter={(e) => {
                  if (streamingStatus === 'delayed') {
                    e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 1)';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (streamingStatus === 'delayed') {
                    e.currentTarget.style.backgroundColor = 'rgba(108, 117, 125, 0.9)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
                title={
                  streamingStatus === 'current'
                    ? 'Transmisión en vivo (< 9s de retraso)'
                    : `Click para ir a EN VIVO (${Math.round(secondsBehind)}s de retraso)`
                }
              >
                {/* Punto indicador pulsante */}
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    animation: streamingStatus === 'current' ? 'pulse 2s ease-in-out infinite' : 'none'
                  }}
                />
                <span>LIVE</span>
                {streamingStatus === 'delayed' && secondsBehind > 0 && (
                  <span style={{ fontSize: '10px', opacity: 0.8 }}>
                    -{Math.round(secondsBehind)}s
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Estilos CSS para la animación del pulso */}
          <style>
            {`
              @keyframes pulse {
                0%, 100% {
                  opacity: 1;
                }
                50% {
                  opacity: 0.5;
                }
              }
            `}
          </style>

          {videoError && (
            <div
              style={{
                position: 'absolute',
                bottom: '10px',
                left: '10px',
                right: '10px',
                zIndex: 2,
                backgroundColor: 'rgba(220, 53, 69, 0.8)',
                padding: '10px',
                borderRadius: '4px',
                color: 'white'
              }}
            >
              <Icon icon="Alert" className="mr-2" />
              {videoError}
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', color: '#6c757d' }}>
          <Icon icon="Monitor" size={48} className="mb-3" />
          <div>{intl.formatMessage({ id: 'units.videoPlayer.noStream' })}</div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayerComponent;
